/**
 * @summary - Component that Provides Attachments for staff details
 * @author Purushothaman T
 * @since 2024-08-09
 */
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import axios from "axios";
import CustomAgGrid from "../commoncomponents/CustomAgGrid";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import { randomId } from "@mui/x-data-grid-generator";
import { Button, Typography, Stack } from "@mui/material";
import { DefaultCard } from "../../assets/styled-components/card";
import { getAuthToken } from "../../utility/tokenUtility";
import { MasterGraph } from "../../utility/apiURLS";
import CustomDialog from "../commoncomponents/CustomDialog";
import SectionTitle from "../commoncomponents/SectionTitle";
import { dialogContent, validations } from "../../utility/message";
import utilities from "../../utility/utilities";

const emptyRow = {
  rowId: "",
  date: "",
  filename: "",
  filetype: "",
  size: "",
  progress: "",
  enableDownload: false,
};
const fillEmptyFields = () => {
  const newRow = {
    ...emptyRow,
    rowId: randomId(),
  };
  return newRow;
};

const isRowEmpty = (row) => {
  return Object.keys(row).every((key) => key === "rowId" || row[key] === "");
};

const FileHandling = forwardRef(
  (
    { isEdit, id, rowData, setRowData, selectedFiles, setSelectedFiles },
    ref
  ) => {
    const fileInputRef = useRef();
    const colDefs = [
      {
        headerName: "Upload",
        field: "attachment",
        cellRenderer: (params) => {
          const isFileUploaded = params.data.filename;
          const handleButtonClick = () => {
            fileInputRef.current.click();
          };
          return (
            <>
              {handleAttachment && (
                <Button
                  variant="outlined"
                  sx={{ marginBottom: "10px", marginTop: "10px" }}
                  onClick={handleButtonClick}
                  disabled={isFileUploaded}
                >
                  {isFileUploaded ? "File Uploaded" : "Browse File"}
                </Button>
              )}
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={(e) => handleFileChange(e, params.data.rowId)}
              />
            </>
          );
        },
        sortable: false,
        filter: false,
      },
      {
        field: "date",
        headerName: "Date",
      },
      { field: "filename", headerName: "File Name" },
      { field: "filetype", headerName: "File Type" },
      { field: "size", headerName: "Size" },
    ];
    const fetchAttachments = async () => {
      try {
        const config = {
          headers: {
            // Attach bearer token to request header
            Authorization: `Bearer ${token}`,
          },
        };
        const response = await axios.get(
          `${MasterGraph}/api/GraphMasemployeedetails/GetEmployeeAttachmentList/${id}`,
          config
        );
        setRowData(
          response.data.length === 0
            ? [fillEmptyFields()]
            : response.data.map((data) => {
                return {
                  ...data,
                  size: data.filesize,
                  filename: data.documenttitle?.split(".")[0],
                  filetype: data.documenttype,
                  date: utilities.formatDate({
                    dateString: data.createddate,
                    format: "dd/mm/yyyy",
                    dbdate: true,
                  }),
                  downloadId: data.filename,
                  enableDownload: true,
                };
              })
        );
      } catch (error) {}
    };

    useEffect(() => {
      if (isEdit) {
        fetchAttachments();
      } else {
        setRowData([fillEmptyFields()]);
      }
    }, [isEdit]);

    // useEffect(() => {
    //   if (rowData.length === 0) {
    //     setRowData([fillEmptyFields()]);
    //   }
    // }, [rowData]);

    const [openDialog, setOpenDialog] = useState(false);
    const [contentDialog, setContentDialog] = useState("");
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const [attachment, setAttachment] = useState("");
    const [gridApi, setGridApi] = useState(null);
    const token = getAuthToken("id_token");

    const allowedFileExtensions = [
      "jpeg",
      "jpg",
      "png",
      "xlsx",
      "csv",
      "pdf",
      "doc",
    ];

    const handleDelete = (data) => {
      if (isRowEmpty(data) || !data.enableDownload) {
        if (rowData.length !== 1) {
          setRowData((prev) =>
            prev.filter((item) => item.rowId !== data.rowId)
          );
          setSelectedFiles((prev) =>
            prev.filter((item) => item.rowId !== data.rowId)
          );
        } else if (rowData.length === 1 && data.file) {
          setRowData((prev) =>
            prev.map((row) => ({
              rowId: row.rowId,
              date: "",
              filename: "",
              filetype: "",
              size: "",
              progress: "",
              enableDownload: false,
            }))
          );
        }
      } else {
        setOpenConfirmDialog(true);
        setContentDialog(dialogContent.confirmDelete);
        setAttachment(data);
      }
    };
    const handleAttachment = () => {};
    const handleAddFile = () => {
      setRowData((prev) => [...prev, fillEmptyFields()]);
    };

    const handleFileChange = (event, rowId) => {
      const file = event.target.files[0];
      if (!file) return;

      const fileName = file.name;
      const fileExtension = fileName.split(".").pop();
      if (!allowedFileExtensions.includes(fileExtension)) {
        setOpenDialog(true);
        setContentDialog(
          "Only DOC, JPEG, JPG, PNG, XLSX, CSV, and PDF files are allowed."
        );
        return;
      }
      if (file.size > 20 * 1024 * 1024) {
        setOpenDialog(true);
        setContentDialog(
          "File size exceeds 20 MB. Please select smaller files."
        );
        return;
      }

      const fileNameWithoutExtension = fileName.replace(
        "." + fileExtension,
        ""
      );

      setSelectedFiles([...selectedFiles, { rowId, file }]);

      const updatedRows = rowData.map((row) => {
        if (row.rowId === rowId) {
          return {
            ...row,
            file,
            filename: fileNameWithoutExtension,
            filetype: fileExtension.toUpperCase(),
            size: (file.size / (1024 * 1024)).toFixed(2) + " MB",
            date: utilities.getCurrentDateFormatted("DD/MM/YYYY"),
          };
        }
        return row;
      });

      setRowData(updatedRows);
    };

    const registerFiles = async () => {
      const token = getAuthToken("id_token");
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      let storeFileNameArray = [];
      let storeregsitredFilesResponse = [];
      let storefileUploadResponse = [];

      for (let i = 0; i < selectedFiles?.length; i++) {
        const formData = new FormData();
        const extension = selectedFiles[i]?.file?.name.split(".").pop();
        const fileName = uuidv4(extension); // generate a unique id
        formData.append("fileObj", selectedFiles[i]?.file, fileName); // append file with unique id as filename
        storeFileNameArray.push({
          id: fileName,
          filename: `${selectedFiles[i]?.file?.name}`,
          selectedFileId: selectedFiles[i]?.id,
        });

        try {
          const fileUploadResponse = await axios.post(
            `${MasterGraph}/api/fileupload`,
            formData,
            config
          );
          storeregsitredFilesResponse.push(fileUploadResponse.data);
          storefileUploadResponse.push(fileUploadResponse.data);
        } catch (error) {
          console.log(
            "Error uploading file:",
            error.response ? error.response.data : error.message
          );
        }
      }
      return { storefileUploadResponse, storeFileNameArray };
    };

    const handleSubmitClick = async (postData) => {
      if (selectedFiles.length > 0) {
        try {
          const { storefileUploadResponse, storeFileNameArray } =
            await registerFiles();
          if (storefileUploadResponse && storefileUploadResponse.length > 0) {
            let storefileUploadResponseFinal = [];
            for (let i = 0; i < storefileUploadResponse.length; i++) {
              const file = storefileUploadResponse[i];
              const documentTitle = storeFileNameArray.find(
                (item) => item.id === file.fileName
              )?.filename;

              if (documentTitle) {
                const payload = {
                  filename: file.fileName,
                  filesize: file.fileSize,
                  filepath: "D:/IFAC-X/UploadFolder",
                  documenttitle: documentTitle,
                  documentType: documentTitle.split(".")[1].toUpperCase(),
                  createdBy: postData.modifiedBy,
                  masemployeedetails: postData,
                };
                try {
                  const response = await saveFilesToAttachment(payload);
                  if (response) {
                    storefileUploadResponseFinal.push(response);
                  } else {
                    console.log("Error saving file:", file.fileName);
                  }
                } catch (error) {
                  console.log("Error saving file:", error);
                }
              } else {
                console.log(
                  "Document Title not found for file:",
                  file.fileName
                );
              }
            }
            if (
              storefileUploadResponseFinal?.length > 0 &&
              storefileUploadResponseFinal?.length ===
                storefileUploadResponse?.length
            ) {
              fetchAttachments();
            } else {
              console.log("Error in uploading some files");
            }
          } else {
            console.log("No files registered");
          }
        } catch (error) {
          console.log("Error registering files:", error);
        }
      } else {
        console.log("No files selected for upload");
      }
    };

    const saveFilesToAttachment = async (payload) => {
      const config = {
        headers: {
          Authorization: `Bearer ${getAuthToken("id_token")}`,
        },
      };

      try {
        const response = await axios.post(
          `${MasterGraph}/api/masemployeeattachments`,
          payload,
          config
        );
        return response.data;
      } catch (error) {
        console.log("saveFilesToAttachment Save error:", error);
      }
    };

    useImperativeHandle(ref, () => ({
      handleSubmitClick,
    }));

    function uuidv4(extension) {
      return (
        "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
          var r = (Math.random() * 16) | 0,
            v = c === "x" ? r : (r & 0x3) | 0x8;
          return v.toString(16);
        }) +
        "." +
        extension
      );
    }

    const handleDialogConfirm = async () => {
      try {
        const token = getAuthToken("id_token");
        const config = {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        };
        const response = await axios.delete(
          `${MasterGraph}/api/masemployeeattachments/${attachment.id}`,
          config
        );
        if (response.status === 204) {
          setOpenDialog(true);
          setContentDialog(dialogContent.deleted);
          fetchAttachments();
        }
      } catch (error) {
        if (error.response.status === 400) {
          setOpenDialog(true);
          setContentDialog(error.response.data.detail);
        }
        return error.response;
      }
    };

    const handleCloseDialog = () => {
      setOpenDialog(false);
      setOpenConfirmDialog(false);
    };
    const handleDownload = async (data) => {
      console.log(data);

      try {
        const token = getAuthToken("id_token");
        const config = {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          responseType: "blob", // Ensure responseType is part of the config
        };

        const response = await axios.get(
          `${MasterGraph}/api/FileUpload/DownloadFile/${data.downloadId}`,
          config
        );

        if (response.status === 200) {
          // Determine the correct file extension from the content type
          const contentType = response.headers["content-type"];
          const extension = utilities.getExtensionFromContentType(contentType);

          // Create a URL for the blob data
          const blobUrl = window.URL.createObjectURL(response.data);

          // Construct the filename with the correct extension
          const filename = `${data.filename}${extension}`;

          // Create a link element
          const link = document.createElement("a");
          link.href = blobUrl;
          link.download = filename; // Set the correct filename

          // Append link to the body
          document.body.appendChild(link);

          // Trigger click to download the file
          link.click();

          // Clean up and remove link
          link.remove();
          window.URL.revokeObjectURL(blobUrl);
        } else {
          setOpenDialog(true);
          setContentDialog(validations.default);
        }
      } catch (error) {
        console.log("Error downloading file:", error);
        return error.response;
      }
    };

    return (
      <>
        <DefaultCard mt={2}>
          <Stack>
            <SectionTitle title={"Attachments"} />
            <Typography variant="labelSm" mt={0}>
              Please add files in format of doc, jpg, png, pdf, xlsx, csv and
              file size should be less than 20 MB.
            </Typography>
          </Stack>
          <Stack direction={"row"} justifyContent={"end"}>
            <Stack mb={1}>
              <Button variant="contained" onClick={handleAddFile}>
                <AddRoundedIcon />
                Add File
              </Button>
            </Stack>
          </Stack>
          <CustomAgGrid
            handleAttachment={handleFileChange}
            columns={colDefs}
            rows={rowData}
            refSet={gridApi}
            setGridApi={setGridApi}
            handleDelete={handleDelete}
            handleDownload={handleDownload}
          />
        </DefaultCard>
        <CustomDialog
          open={openDialog}
          onClose={handleCloseDialog}
          content={contentDialog}
          confirmBtn={"Ok"}
          onConfirm={handleCloseDialog}
        />
        <CustomDialog
          open={openConfirmDialog}
          onClose={handleCloseDialog}
          content={contentDialog}
          confirmBtn={"Yes"}
          cancelBtn={"No"}
          onConfirm={handleDialogConfirm}
        />
      </>
    );
  }
);
export default FileHandling;
